import {
  DescriptionLine,
  normalizeRuns,
  parseMobileDescription,
  runLength,
  splitRuns,
  TextRun
} from './mobileDescription'
import { insertLines } from './mobileDescriptionInsert'

export interface EditorLine extends DescriptionLine {
  id: string
}

export interface EditorCaret {
  id: string
  offset: number
}

export interface EditorChange {
  lines: EditorLine[]
  caret: EditorCaret | null
  structural: boolean
}

let lineSequence = 0

export const createLineId = (): string => {
  lineSequence += 1
  return `mobile-description-${lineSequence}`
}

export const toEditorLines = (lines: DescriptionLine[]): EditorLine[] =>
  lines.map((line) => ({ ...line, id: createLineId() }))

export const emptyEditorLine = (): EditorLine => ({
  id: createLineId(),
  kind: 'text',
  marker: '*',
  runs: [{ text: '', bold: false }]
})

const lineAt = (lines: EditorLine[], index: number): EditorLine | null => {
  if (index < 0 || index >= lines.length) {
    return null
  }
  return lines[index]
}

const isEditorLine = (line: DescriptionLine): line is EditorLine =>
  'id' in line && typeof (line as EditorLine).id === 'string'

interface KeyInput {
  lines: EditorLine[]
  id: string
  key: string
  offset: number | null
  collapsed: boolean
}

interface EnterInput {
  lines: EditorLine[]
  line: EditorLine
  index: number
  offset: number
}

const enterChange = ({ lines, line, index, offset }: EnterInput): EditorChange => {
  if (line.kind === 'bullet' && runLength(line.runs) === 0) {
    return {
      lines: lines.map((item) => (item.id === line.id ? { ...item, kind: 'text' } : item)),
      caret: null,
      structural: false
    }
  }
  const [left, right] = splitRuns(line.runs, offset)
  const created: EditorLine = {
    id: createLineId(),
    kind: line.kind === 'bullet' ? 'bullet' : 'text',
    marker: line.kind === 'bullet' ? line.marker : '*',
    runs: right
  }
  const next = lines.slice()
  next[index] = { ...line, runs: left }
  next.splice(index + 1, 0, created)
  return { lines: next, caret: { id: created.id, offset: 0 }, structural: true }
}

const backspaceChange = (lines: EditorLine[], line: EditorLine, index: number): EditorChange | null => {
  if (line.kind === 'bullet') {
    return {
      lines: lines.map((item) => (item.id === line.id ? { ...item, kind: 'text' } : item)),
      caret: null,
      structural: false
    }
  }
  const previous = lineAt(lines, index - 1)
  if (previous === null) {
    return null
  }
  if (previous.kind === 'rule') {
    return {
      lines: lines.filter((item) => item.id !== previous.id),
      caret: { id: line.id, offset: 0 },
      structural: true
    }
  }
  return {
    lines: [
      ...lines.slice(0, index - 1),
      { ...previous, runs: normalizeRuns([...previous.runs, ...line.runs]) },
      ...lines.slice(index + 1)
    ],
    caret: { id: previous.id, offset: runLength(previous.runs) },
    structural: true
  }
}

export const removeLineChange = (lines: EditorLine[], id: string): EditorChange => {
  if (lines.length === 1) {
    const blank = emptyEditorLine()
    return { lines: [blank], caret: { id: blank.id, offset: 0 }, structural: true }
  }
  const index = lines.findIndex((line) => line.id === id)
  const next = lines.filter((line) => line.id !== id)
  const focus = lineAt(next, Math.max(index - 1, 0)) ?? emptyEditorLine()
  return {
    lines: next,
    caret: { id: focus.id, offset: focus.kind === 'rule' ? 0 : runLength(focus.runs) },
    structural: true
  }
}

const deleteForwardChange = (lines: EditorLine[], line: EditorLine, index: number): EditorChange | null => {
  if (line.kind === 'rule') {
    return removeLineChange(lines, line.id)
  }
  const next = lineAt(lines, index + 1)
  if (next === null) {
    return null
  }
  if (next.kind === 'rule') {
    return removeLineChange(lines, next.id)
  }
  return {
    lines: [
      ...lines.slice(0, index),
      { ...line, runs: normalizeRuns([...line.runs, ...next.runs]) },
      ...lines.slice(index + 2)
    ],
    caret: { id: line.id, offset: runLength(line.runs) },
    structural: true
  }
}

export const keyChange = (input: KeyInput): EditorChange | null => {
  const index = input.lines.findIndex((line) => line.id === input.id)
  const line = lineAt(input.lines, index)
  if (line === null) {
    return null
  }
  if (input.key === 'Enter') {
    return enterChange({ lines: input.lines, line, index, offset: input.offset ?? 0 })
  }
  if (input.key === 'Backspace' && input.collapsed && input.offset === 0) {
    return backspaceChange(input.lines, line, index)
  }
  if (input.key === 'Delete' && input.collapsed && input.offset === runLength(line.runs)) {
    return deleteForwardChange(input.lines, line, index)
  }
  return null
}

export const clipboardIsStructured = (clipboard: string): boolean =>
  /[\n\r]/.test(clipboard) || clipboard.includes('\\\\') || clipboard.includes('**') || clipboard.includes('----')

interface PasteInput {
  lines: EditorLine[]
  id: string
  offset: number
  clipboard: string
}

export const pasteChange = ({ lines, id, offset, clipboard }: PasteInput): EditorChange | null => {
  const index = lines.findIndex((line) => line.id === id)
  const current = lineAt(lines, index)
  if (current === null) {
    return null
  }
  const result = insertLines({ lines, index, offset, pasted: parseMobileDescription(clipboard) })
  const next = result.lines.map((line, lineIndex) => {
    if (isEditorLine(line)) {
      return line
    }
    if (lineIndex === index) {
      return { ...line, id: current.id }
    }
    return { ...line, id: createLineId() }
  })
  const target = lineAt(next, result.caret.index)
  return {
    lines: next,
    caret: target === null ? null : { id: target.id, offset: result.caret.offset },
    structural: true
  }
}

const listTargets = (lines: EditorLine[], ids: string[]): EditorLine[] =>
  lines.filter((line) => ids.includes(line.id) && line.kind !== 'rule')

const withListKind = (line: EditorLine, ids: string[], makeBullets: boolean): EditorLine => {
  if (!ids.includes(line.id) || line.kind === 'rule') {
    return line
  }
  if (!makeBullets) {
    return { ...line, kind: 'text' }
  }
  return { ...line, kind: 'bullet', marker: '*' }
}

export const toggleListChange = (lines: EditorLine[], ids: string[]): EditorChange | null => {
  const targets = listTargets(lines, ids)
  if (targets.length === 0) {
    return null
  }
  const makeBullets = targets.some((line) => line.kind !== 'bullet')
  return {
    lines: lines.map((line) => withListKind(line, ids, makeBullets)),
    caret: null,
    structural: false
  }
}

export const insertRuleChange = (lines: EditorLine[], id: string | null): EditorChange => {
  const found = id === null ? -1 : lines.findIndex((line) => line.id === id)
  const at = found < 0 ? lines.length - 1 : found
  const rule: EditorLine = { id: createLineId(), kind: 'rule', marker: '*', runs: [{ text: '', bold: false }] }
  const next = [...lines.slice(0, at + 1), rule, ...lines.slice(at + 1)]
  return { lines: next, caret: { id: rule.id, offset: 0 }, structural: true }
}

export const insertTextAfterChange = (lines: EditorLine[], id: string): EditorChange => {
  const created = emptyEditorLine()
  const index = lines.findIndex((line) => line.id === id)
  const next = lines.slice()
  next.splice(index + 1, 0, created)
  return { lines: next, caret: { id: created.id, offset: 0 }, structural: true }
}

export const replaceRuns = (lines: EditorLine[], id: string, runs: TextRun[]): EditorLine[] =>
  lines.map((line) => (line.id === id ? { ...line, runs } : line))
